import { useUser } from '@auth0/nextjs-auth0';
import { type ReferenceDataParams, type Suburb } from '@lib/types/shared';
import { callApi } from '@lib/utils/api';
import { useQuery, UseQueryResult } from '@tanstack/react-query';

export type CountryInfo = {
  countryCode: string;
  countryName: string;
  currencyCode: string;
};

export type AccountInfo = {
  id: string;
  name: string;
  email: string;
  timeZoneId: string;
  profilePhotoUrl: string | null;
  stripeId: string | null;
  metadata: {
    AirtableItemId: string;
  };
  airtableItemId: string;
  countryInfo?: CountryInfo;
};

export type UserInfo = {
  id: string;
  name: string;
  email: string;
  role: string;
  photoUrl: string | null;
  auth0Id: string | null;
  metadata: {
    AirtableItemId: string;
  };
  airtableItemId: string;
};

type CurrentUserProfileResponse = {
  result: null;
  value: {
    accountInfo: AccountInfo;
    userInfo: UserInfo;
  };
};

export const getCurrentUserProfile = async (): Promise<{
  accountInfo: AccountInfo;
  userInfo: UserInfo;
}> => {
  const response = await callApi<CurrentUserProfileResponse>(
    '/api/rest/petOwners/currentUserProfile',
    { method: 'GET' }
  );

  return response.value;
};

export const useCurrentUserProfile = (options?: {
  enabled?: boolean;
}): UseQueryResult<{
  accountInfo: AccountInfo;
  userInfo: UserInfo;
} | null> => {
  // Check if user is logged in with Auth0
  const { user, isLoading: isUserLoading } = useUser();
  const isEnabled = options?.enabled !== false; // Default to true if not specified

  return useQuery({
    queryKey: ['currentUserProfile'],
    queryFn: getCurrentUserProfile,
    // Only fetch if user is logged in and enabled
    enabled: !!user && !isUserLoading && isEnabled,
    // Keep previous data while fetching new data
    placeholderData: (previousData) => previousData,
    // User profile doesn't change often, so we can cache it for a while
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Return null when user is not logged in
    select: (data) => {
      if (!user) return null;
      return data;
    },
    // Don't show error for anonymous users - just return null
    retry: false,
  });
};

export const getSuburbs = async (
  params?: ReferenceDataParams
): Promise<Suburb[]> => {
  try {
    const queryParams = new URLSearchParams();

    if (params?.filter && params.filter.trim())
      queryParams.append('filter', params.filter);
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const response = await callApi<{ data: Suburb[] }>(
      `/api/rest/referenceData/suburbs/search?${queryParams.toString()}`,
      { method: 'GET' }
    );

    return response.data || [];
  } catch (error) {
    console.error('Error fetching suburbs:', error);
    return [];
  }
};
